import React, { useState, useEffect, useCallback, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_TI } from '../../shared/styles';
import Portal from '../../shared/Portal';

// ── Dark form palette ─────────────────────────────────────────────────────────
const F = {
  bg:          '#14141a',
  panel:       '#1e1e28',
  border:      'rgba(255,255,255,0.1)',
  input:       '#12121a',
  inputBorder: 'rgba(255,255,255,0.15)',
};

// ── Constants ─────────────────────────────────────────────────────────────────
const DEFAULT_CATS = [
  { id: 'cat_general',     name: 'General',     parent: null },
  { id: 'cat_electronics', name: 'Electronics', parent: null },
  { id: 'cat_clothing',    name: 'Clothing',    parent: null },
  { id: 'cat_food',        name: 'Food',        parent: null },
  { id: 'cat_health',      name: 'Health',      parent: null },
  { id: 'cat_beauty',      name: 'Beauty',      parent: null },
  { id: 'cat_sports',      name: 'Sports',      parent: null },
  { id: 'cat_other',       name: 'Other',       parent: null },
];

const VAT_OPTIONS  = [0, 6, 9, 21];
const CURRENCIES   = ['EUR', 'USD', 'GBP'];
const STATUSES     = ['active', 'draft', 'archived'];
const CLOTHING_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
const IMAGE_VIEW_TAGS    = ['front', 'back', 'side', 'detail', 'model', 'flat'];
const IMAGE_VIEW_LABEL   = { front: 'Voorkant', back: 'Achterkant', side: 'Zijkant', detail: 'Detail', model: 'Model', flat: 'Plat' };
const IMAGE_VIEW_LABEL_EN = { front: 'Front', back: 'Back', side: 'Side', detail: 'Detail', model: 'Model', flat: 'Flat' };

const STATUS_CFG = {
  active:   { nl: 'Actief',       en: 'Active',   color: '#4AC87A' },
  draft:    { nl: 'Concept',      en: 'Draft',    color: '#C8A84B' },
  archived: { nl: 'Gearchiveerd', en: 'Archived', color: '#667799' },
};

const EMPTY_FORM = {
  name: '', sku: '',
  cat1: '', cat2: '', cat3: '',
  description: '',
  price: '', sale_price: '', currency: 'EUR', vat_pct: 21,
  stock_qty: '', low_stock_alert: '',
  images: ['', '', '', '', ''],
  image_views: ['', '', '', '', ''],
  primary_image: 0,
  distributor_name: '', distributor_contact: '', distributor_lead_days: '',
  discount_type: 'pct', discount_value: '',
  status: 'active', barcode: '', weight_g: '', tags: '',
  clothing_meta: { sizes: [], colors: [] },
  homepage_published: false,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const currencySymbol = (c) => ({ EUR: '€', USD: '$', GBP: '£' }[c] || c);
const formatPrice = (price, currency) =>
  price !== '' && price !== null && price !== undefined
    ? `${currencySymbol(currency)}${parseFloat(price).toFixed(2)}`
    : '—';

const parseImages = (raw) => {
  if (Array.isArray(raw)) {
    const arr = raw.map(x => (typeof x === 'string' ? x : '')).slice(0, 5);
    while (arr.length < 5) arr.push('');
    return arr;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parseImages(parsed);
    } catch (_) {}
    if (raw.trim() !== '') return [raw, '', '', '', ''];
  }
  return ['', '', '', '', ''];
};

const genCatId = () => `cat_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
const categoryPath = (cats, cat1, cat2, cat3) => {
  const parts = [];
  if (cat1) { const c = cats.find(x => x.id === cat1); if (c) parts.push(c.name); }
  if (cat2) { const c = cats.find(x => x.id === cat2); if (c) parts.push(c.name); }
  if (cat3) { const c = cats.find(x => x.id === cat3); if (c) parts.push(c.name); }
  return parts.join(' > ');
};

// ── Image Carousel ────────────────────────────────────────────────────────────
function ImageCarousel({ images, primaryIdx = 0, height = 200 }) {
  const imgs = parseImages(images).filter(x => x.trim() !== '');
  const start = imgs.length > 0 && imgs[primaryIdx] ? primaryIdx : 0;
  const [active, setActive] = useState(start);

  useEffect(() => {
    const i = imgs.length > 0 && imgs[primaryIdx] ? primaryIdx : 0;
    setActive(Math.min(i, Math.max(0, imgs.length - 1)));
  }, [images, primaryIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!imgs.length) return null;

  return (
    <div style={{ position: 'relative', width: '100%', borderRadius: 14, overflow: 'hidden', background: '#0d0d14', marginBottom: 14 }}>
      <img
        src={imgs[active]}
        alt=""
        style={{ width: '100%', height, objectFit: 'contain', display: 'block' }}
        onError={e => { e.target.style.display = 'none'; }}
      />
      {imgs.length > 1 && (
        <>
          <button onClick={() => setActive(i => (i - 1 + imgs.length) % imgs.length)}
            style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontSize: 16, lineHeight: '30px', textAlign: 'center', padding: 0 }}>‹</button>
          <button onClick={() => setActive(i => (i + 1) % imgs.length)}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontSize: 16, lineHeight: '30px', textAlign: 'center', padding: 0 }}>›</button>
          <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5 }}>
            {imgs.map((_, i) => (
              <div key={i} onClick={() => setActive(i)} style={{ width: 6, height: 6, borderRadius: '50%', background: i === active ? '#fff' : 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'background 0.2s' }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function StatusBadge({ status, nl: isNl }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.active;
  return (
    <span style={{ background: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}44`, borderRadius: 6, padding: '1px 7px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {isNl ? cfg.nl : cfg.en}
    </span>
  );
}

function StockBadge({ qty, alert, nl: isNl }) {
  if (qty === '' || qty === null || qty === undefined) return null;
  const n = parseInt(qty); const a = parseInt(alert);
  const low = !isNaN(a) && n <= a;
  return (
    <span style={{ color: low ? '#C8603A' : C.silver, fontSize: 11 }}>
      {isNl ? 'Voorraad' : 'Stock'}: {n}{low ? ' ⚠️' : ''}
    </span>
  );
}

// ── Product Card ──────────────────────────────────────────────────────────────
function ProductCard({ product, onClick, nl: isNl }) {
  const imgs    = parseImages(product.images).filter(x => x.trim() !== '');
  const primIdx = typeof product.primary_image === 'number' ? product.primary_image : 0;
  const thumb   = imgs[primIdx] || imgs[0];

  return (
    <div
      onClick={onClick}
      style={{
        background: _T.bg3,
        border: `1px solid ${_T.cardBorderBot}`,
        borderRadius: 16,
        padding: '14px 16px',
        marginBottom: 10,
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      {/* Thumbnail with photo count badge */}
      <div style={{ position: 'relative', width: 62, height: 62, borderRadius: 12, flexShrink: 0, background: _T.bg2, border: `1px solid ${_T.cardBorderBot}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {thumb
          ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
          : <span style={{ fontSize: 26 }}>📦</span>
        }
        {imgs.length > 1 && (
          <div style={{ position: 'absolute', bottom: 2, right: 2, background: 'rgba(0,0,0,0.75)', borderRadius: 5, padding: '1px 5px', fontSize: 9, color: '#fff', fontWeight: 700 }}>
            {imgs.length}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
          <span style={{ color: C.white, fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
            {product.name || (isNl ? 'Naamloos' : 'Unnamed')}
          </span>
          <StatusBadge status={product.status || 'active'} nl={isNl} />
        </div>
        {product.sku && <div style={{ color: C.silver, fontSize: 11, marginBottom: 3 }}>SKU: {product.sku}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: _T.accent, fontSize: 13, fontWeight: 700 }}>
            {formatPrice(product.price, product.currency || 'EUR')}
          </span>
          {product.sale_price !== '' && product.sale_price !== null && product.sale_price !== undefined && (
            <span style={{ color: C.silver, fontSize: 12, textDecoration: 'line-through' }}>
              {formatPrice(product.sale_price, product.currency || 'EUR')}
            </span>
          )}
          <StockBadge qty={product.stock_qty} alert={product.low_stock_alert} nl={isNl} />
        </div>
      </div>

      <div style={{ color: C.silver, fontSize: 18, flexShrink: 0 }}>›</div>
    </div>
  );
}

// ── Slide-up Sheet ────────────────────────────────────────────────────────────
function Sheet({ open, onClose, children }) {
  if (!open) return null;
  return (
    <Portal>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        <style>{CSS}</style>
        <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
        <div style={{
          background: F.bg,
          borderTop: `1.5px solid ${F.border}`,
          borderRadius: '20px 20px 0 0',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1) both',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', padding: '12px 0 6px' }} onClick={onClose}>
            <div style={{ width: 44, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.2)', cursor: 'pointer' }} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 18px env(safe-area-inset-bottom,24px)', paddingBottom: 40 }}>
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ── Category Select ───────────────────────────────────────────────────────────
function CatSelect({ level, cats, parentId, value, onChange, nl: isNl, inputStyle }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const options = cats.filter(c => level === 1 ? c.parent === null : c.parent === parentId);
  if (level > 1 && !parentId) return null;
  const labelMap = { 1: isNl ? 'Categorie' : 'Category', 2: isNl ? 'Subcategorie' : 'Subcategory', 3: isNl ? 'Subniveau' : 'Sub-level' };
  const handleConfirmAdd = () => {
    if (!newName.trim()) return;
    onChange('__add__', newName.trim());
    setNewName(''); setAdding(false);
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ color: C.silver, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 5 }}>{labelMap[level]}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select style={{ ...inputStyle, flex: 1 }} value={value} onChange={e => onChange(e.target.value)}>
          <option value="">{isNl ? '— Geen —' : '— None —'}</option>
          {options.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {!adding && (
          <button onClick={() => setAdding(true)} style={{ background: 'transparent', border: `1px solid ${F.inputBorder}`, borderRadius: 8, padding: '7px 10px', color: _T.accent, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            + {isNl ? 'Nieuw' : 'New'}
          </button>
        )}
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input autoFocus style={{ ...inputStyle, flex: 1 }} value={newName} onChange={e => setNewName(e.target.value)}
            placeholder={isNl ? 'Naam...' : 'Name...'}
            onKeyDown={e => { if (e.key === 'Enter') handleConfirmAdd(); if (e.key === 'Escape') { setAdding(false); setNewName(''); } }} />
          <button onClick={handleConfirmAdd} disabled={!newName.trim()} style={{ background: _T.accent, border: 'none', borderRadius: 8, padding: '7px 12px', color: '#0a1220', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: !newName.trim() ? 0.5 : 1, flexShrink: 0 }}>OK</button>
          <button onClick={() => { setAdding(false); setNewName(''); }} style={{ background: 'transparent', border: `1px solid ${F.inputBorder}`, borderRadius: 8, padding: '7px 10px', color: C.silver, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>×</button>
        </div>
      )}
    </div>
  );
}

// ── Clothing Fields ───────────────────────────────────────────────────────────
function ClothingFields({ form, setForm, nl: isNl, inputStyle, P }) {
  const meta   = form.clothing_meta || { sizes: [], colors: [] };
  const sizes  = meta.sizes  || [];
  const colors = meta.colors || [];
  const setMeta = (updater) => setForm(f => ({ ...f, clothing_meta: updater(f.clothing_meta || { sizes: [], colors: [] }) }));
  const toggleSize = (sz) => setMeta(m => ({ ...m, sizes: m.sizes.includes(sz) ? m.sizes.filter(s => s !== sz) : [...m.sizes, sz] }));
  const addColor = () => setMeta(m => ({ ...m, colors: [...(m.colors || []), { name: '', hex: '#ffffff' }] }));
  const updateColor = (idx, key, val) => setMeta(m => { const cols = [...(m.colors || [])]; cols[idx] = { ...cols[idx], [key]: val }; return { ...m, colors: cols }; });
  const removeColor = (idx) => setMeta(m => ({ ...m, colors: (m.colors || []).filter((_, i) => i !== idx) }));

  return (
    <>
      <div style={{ color: C.white, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{isNl ? 'Maten' : 'Sizes'}</div>
      <div style={{ ...P, display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {CLOTHING_SIZES.map(sz => {
          const active = sizes.includes(sz);
          return (
            <button key={sz} onClick={() => toggleSize(sz)} style={{ background: active ? `${_T.accent}22` : 'transparent', border: `1.5px solid ${active ? _T.accent : 'rgba(255,255,255,0.2)'}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', color: active ? _T.accent : C.silver, fontSize: 13, fontWeight: active ? 700 : 400 }}>
              {sz}
            </button>
          );
        })}
      </div>

      <div style={{ color: C.white, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{isNl ? 'Kleuren' : 'Colors'}</div>
      <div style={P}>
        {colors.map((col, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input type="color" value={col.hex || '#ffffff'} onChange={e => updateColor(idx, 'hex', e.target.value)} style={{ width: 38, height: 34, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2, background: 'transparent', flexShrink: 0 }} />
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: col.hex || '#fff', border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0 }} />
            <input style={{ ...inputStyle, flex: 1 }} value={col.name || ''} onChange={e => updateColor(idx, 'name', e.target.value)} placeholder={isNl ? 'Naam kleur...' : 'Color name...'} />
            <button onClick={() => removeColor(idx)} style={{ background: 'transparent', border: 'none', color: C.silver, fontSize: 18, cursor: 'pointer', flexShrink: 0 }}>×</button>
          </div>
        ))}
        <button onClick={addColor} style={{ background: 'transparent', border: `1px dashed rgba(255,255,255,0.2)`, borderRadius: 10, padding: '8px 16px', color: _T.accent, fontSize: 12, cursor: 'pointer', width: '100%', marginTop: 4 }}>
          + {isNl ? 'Kleur toevoegen' : 'Add color'}
        </button>
      </div>
    </>
  );
}

// ── Product Form (tabbed) ─────────────────────────────────────────────────────
function ProductForm({ form, setForm, cats, setCats, businessId, isClothing, onSave, onDelete, onCancel, saving, isEdit, nl: isNl }) {
  const [tab, setTab] = useState('info');
  const fileRefs = useRef([null, null, null, null, null]);
  const [uploadingSlots, setUploadingSlots] = useState([false, false, false, false, false]);
  const [uploadError, setUploadError] = useState('');

  const inp = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const setImage = (idx, val) => setForm(f => {
    const imgs = [...f.images]; imgs[idx] = val; return { ...f, images: imgs };
  });
  const clearImage = (idx) => setImage(idx, '');

  const handleFileUpload = async (idx, file) => {
    if (!file) return;
    setUploadingSlots(s => { const n = [...s]; n[idx] = true; return n; });
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.url) {
        setImage(idx, data.url);
      } else {
        setUploadError(data.error || (isNl ? 'Upload mislukt.' : 'Upload failed.'));
      }
    } catch (_) {
      setUploadError(isNl ? 'Upload mislukt.' : 'Upload failed.');
    }
    setUploadingSlots(s => { const n = [...s]; n[idx] = false; return n; });
  };

  const handleCatChange = (level, val, newName) => {
    if (val === '__add__') {
      const parentId = level === 1 ? null : level === 2 ? form.cat1 : form.cat2;
      const newCat = { id: genCatId(), name: newName, parent: parentId };
      const updated = [...cats, newCat];
      setCats(updated);
      localStorage.setItem(`sl_cats_${businessId}`, JSON.stringify(updated));
      if (level === 1) setForm(f => ({ ...f, cat1: newCat.id, cat2: '', cat3: '' }));
      else if (level === 2) setForm(f => ({ ...f, cat2: newCat.id, cat3: '' }));
      else setForm(f => ({ ...f, cat3: newCat.id }));
    } else {
      if (level === 1) setForm(f => ({ ...f, cat1: val, cat2: '', cat3: '' }));
      else if (level === 2) setForm(f => ({ ...f, cat2: val, cat3: '' }));
      else setForm(f => ({ ...f, cat3: val }));
    }
  };

  // ── Shared styles ──
  const IS = {
    background: F.input, border: `1px solid ${F.inputBorder}`,
    borderRadius: 10, padding: '9px 12px', color: C.white,
    fontSize: 13, width: '100%', boxSizing: 'border-box',
    outline: 'none', fontFamily: 'Inter,system-ui,sans-serif',
  };
  const P = { background: F.panel, border: `1px solid ${F.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 14 };
  const lbl = { color: C.silver, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 5 };
  const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 };

  const TABS = [
    { id: 'info',    icon: '📋', nl: 'Info',   en: 'Info'    },
    { id: 'pricing', icon: '💶', nl: 'Prijs',  en: 'Pricing' },
    { id: 'photos',  icon: '📷', nl: "Foto's", en: 'Photos'  },
    { id: 'more',    icon: '⚙️',  nl: 'Meer',   en: 'More'    },
  ];

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 800 }}>
            {isEdit ? (isNl ? 'Product bewerken' : 'Edit Product') : (isNl ? 'Nieuw product' : 'New Product')}
          </div>
          {form.name.trim() && (
            <div style={{ color: C.silver, fontSize: 12, marginTop: 3, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {form.name}
            </div>
          )}
        </div>
        <button onClick={onCancel} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: C.silver, fontSize: 18, cursor: 'pointer', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 20, background: 'rgba(255,255,255,0.06)', borderRadius: 14, padding: 4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '8px 4px', borderRadius: 10, border: 'none',
            background: tab === t.id ? _T.accent : 'transparent',
            color: tab === t.id ? '#0a1220' : C.silver,
            fontSize: 10, fontWeight: tab === t.id ? 700 : 400, cursor: 'pointer',
            transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          }}>
            <span style={{ fontSize: 15 }}>{t.icon}</span>
            <span>{isNl ? t.nl : t.en}</span>
          </button>
        ))}
      </div>

      {/* ── INFO TAB ── */}
      {tab === 'info' && (
        <div>
          {/* Name */}
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>{isNl ? 'Naam *' : 'Name *'}</label>
            <input style={{ ...IS, fontSize: 15, fontWeight: 600 }} value={form.name} onChange={inp('name')} placeholder={isNl ? 'Productnaam...' : 'Product name...'} />
          </div>

          <div style={g2}>
            <div>
              <label style={lbl}>SKU</label>
              <input style={IS} value={form.sku} onChange={inp('sku')} placeholder="SKU-001" />
            </div>
            <div>
              <label style={lbl}>{isNl ? 'Status' : 'Status'}</label>
              <select style={IS} value={form.status} onChange={inp('status')}>
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_CFG[s]?.[isNl ? 'nl' : 'en'] || s}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>{isNl ? 'Omschrijving' : 'Description'}</label>
            <textarea style={{ ...IS, minHeight: 80, resize: 'vertical' }} value={form.description} onChange={inp('description')} placeholder={isNl ? 'Korte omschrijving...' : 'Short description...'} />
          </div>

          {/* Categories */}
          <div style={{ ...P, marginBottom: 12 }}>
            <div style={{ color: C.silver2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{isNl ? 'Categorieën' : 'Categories'}</div>
            <CatSelect level={1} cats={cats} parentId={null} value={form.cat1} onChange={(v, n) => handleCatChange(1, v, n)} nl={isNl} inputStyle={IS} />
            {form.cat1 && <CatSelect level={2} cats={cats} parentId={form.cat1} value={form.cat2} onChange={(v, n) => handleCatChange(2, v, n)} nl={isNl} inputStyle={IS} />}
            {form.cat1 && form.cat2 && <CatSelect level={3} cats={cats} parentId={form.cat2} value={form.cat3} onChange={(v, n) => handleCatChange(3, v, n)} nl={isNl} inputStyle={IS} />}
          </div>

          <div style={g2}>
            <div>
              <label style={lbl}>{isNl ? 'Barcode (EAN)' : 'Barcode (EAN)'}</label>
              <input style={IS} value={form.barcode} onChange={inp('barcode')} placeholder="8712345678901" />
            </div>
            <div>
              <label style={lbl}>{isNl ? 'Gewicht (gram)' : 'Weight (g)'}</label>
              <input style={IS} type="number" min="0" value={form.weight_g} onChange={inp('weight_g')} placeholder="500" />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>{isNl ? 'Tags (komma-gescheiden)' : 'Tags (comma-separated)'}</label>
            <input style={IS} value={form.tags} onChange={inp('tags')} placeholder={isNl ? 'sport, sale, nieuw' : 'sport, sale, new'} />
          </div>

          {/* Homepage toggle */}
          <div
            style={{ ...P, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 0 }}
            onClick={() => setForm(f => ({ ...f, homepage_published: !f.homepage_published }))}
          >
            <div>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{isNl ? 'Zichtbaar op homepage' : 'Visible on homepage'}</div>
              <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>{isNl ? 'Toon dit product op de publieke pagina' : 'Show this product on the public page'}</div>
            </div>
            <div style={{ width: 44, height: 24, borderRadius: 12, position: 'relative', background: form.homepage_published ? _T.accent : 'rgba(255,255,255,0.1)', transition: 'background 0.2s', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: 2, left: form.homepage_published ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
            </div>
          </div>
        </div>
      )}

      {/* ── PRICING TAB ── */}
      {tab === 'pricing' && (
        <div>
          <div style={P}>
            <div style={{ color: C.silver2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>{isNl ? 'Prijs' : 'Price'}</div>
            <div style={g2}>
              <div>
                <label style={lbl}>{isNl ? 'Prijs (excl. BTW)' : 'Price (ex VAT)'}</label>
                <input style={IS} type="number" min="0" step="0.01" value={form.price} onChange={inp('price')} placeholder="0.00" />
              </div>
              <div>
                <label style={lbl}>{isNl ? 'Actieprijs' : 'Sale Price'}</label>
                <input style={IS} type="number" min="0" step="0.01" value={form.sale_price} onChange={inp('sale_price')} placeholder={isNl ? 'Optioneel' : 'Optional'} />
              </div>
            </div>
            <div style={g2}>
              <div>
                <label style={lbl}>{isNl ? 'Valuta' : 'Currency'}</label>
                <select style={IS} value={form.currency} onChange={inp('currency')}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>{isNl ? 'BTW %' : 'VAT %'}</label>
                <select style={IS} value={form.vat_pct} onChange={inp('vat_pct')}>
                  {VAT_OPTIONS.map(v => <option key={v} value={v}>{v}%</option>)}
                  {!VAT_OPTIONS.includes(Number(form.vat_pct)) && form.vat_pct !== '' && (
                    <option value={form.vat_pct}>{form.vat_pct}% ({isNl ? 'Aangepast' : 'Custom'})</option>
                  )}
                </select>
              </div>
            </div>
          </div>

          <div style={P}>
            <div style={{ color: C.silver2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>{isNl ? 'Korting' : 'Discount'}</div>
            <div style={g2}>
              <div>
                <label style={lbl}>{isNl ? 'Type' : 'Type'}</label>
                <select style={IS} value={form.discount_type} onChange={inp('discount_type')}>
                  <option value="pct">% {isNl ? 'Percentage' : 'Percentage'}</option>
                  <option value="fixed">{isNl ? 'Vast bedrag' : 'Fixed amount'}</option>
                </select>
              </div>
              <div>
                <label style={lbl}>{isNl ? 'Waarde' : 'Value'}</label>
                <input style={IS} type="number" min="0" step="0.01" value={form.discount_value} onChange={inp('discount_value')} placeholder="0" />
              </div>
            </div>
          </div>

          <div style={P}>
            <div style={{ color: C.silver2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>{isNl ? 'Voorraad' : 'Inventory'}</div>
            <div style={g2}>
              <div>
                <label style={lbl}>{isNl ? 'Aantal op voorraad' : 'Stock Qty'}</label>
                <input style={IS} type="number" min="0" value={form.stock_qty} onChange={inp('stock_qty')} placeholder="0" />
              </div>
              <div>
                <label style={lbl}>{isNl ? 'Lage voorraad alert' : 'Low Stock Alert'}</label>
                <input style={IS} type="number" min="0" value={form.low_stock_alert} onChange={inp('low_stock_alert')} placeholder="5" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PHOTOS TAB ── */}
      {tab === 'photos' && (
        <div>
          <ImageCarousel images={form.images} primaryIdx={form.primary_image} height={220} />

          {uploadError && (
            <div style={{ background: '#C0505022', border: '1px solid #C0505044', borderRadius: 8, padding: '8px 12px', color: '#C05050', fontSize: 13, marginBottom: 12 }}>
              {uploadError}
            </div>
          )}

          <div style={P}>
            <div style={{ color: C.silver2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
              {isNl ? 'Afbeeldingen (max 5) · ⭐ = omslagfoto' : 'Images (max 5) · ⭐ = cover'}
            </div>
            {form.images.map((url, idx) => {
              const viewVal = (form.image_views || [])[idx] || '';
              return (
                <div key={idx} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Star */}
                    <button onClick={() => setForm(f => ({ ...f, primary_image: idx }))} title={isNl ? 'Instellen als omslagfoto' : 'Set as cover'} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, padding: '0 2px', flexShrink: 0, opacity: form.primary_image === idx ? 1 : 0.3 }}>⭐</button>
                    {/* Thumbnail */}
                    <div style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0, background: F.input, border: `1px solid ${F.inputBorder}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {url.trim()
                        ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                        : <span style={{ color: C.silver, fontSize: 16 }}>🖼</span>}
                    </div>
                    {/* URL input */}
                    <input style={{ ...IS, flex: 1 }} value={url} onChange={e => setImage(idx, e.target.value)} placeholder={`URL ${idx + 1}`} />
                    {/* Upload */}
                    <button onClick={() => !uploadingSlots[idx] && fileRefs.current[idx]?.click()} disabled={uploadingSlots[idx]} title={isNl ? 'Bestand uploaden' : 'Upload'} style={{ background: F.panel, border: `1px solid ${F.inputBorder}`, borderRadius: 8, padding: '9px 10px', cursor: uploadingSlots[idx] ? 'default' : 'pointer', color: C.silver, fontSize: 14, flexShrink: 0, opacity: uploadingSlots[idx] ? 0.5 : 1 }}>
                      {uploadingSlots[idx] ? '⏳' : '📁'}
                    </button>
                    <input ref={el => { fileRefs.current[idx] = el; }} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileUpload(idx, e.target.files[0])} />
                    {/* Clear */}
                    {url.trim() && <button onClick={() => clearImage(idx)} style={{ background: 'transparent', border: 'none', color: C.silver, fontSize: 18, cursor: 'pointer', flexShrink: 0, padding: '2px 4px' }}>×</button>}
                  </div>
                  {/* View tag (clothing only) */}
                  {isClothing && url.trim() && (
                    <div style={{ marginTop: 6, paddingLeft: 30 }}>
                      <select
                        style={{ ...IS, fontSize: 11, padding: '5px 8px', width: 'auto' }}
                        value={viewVal}
                        onChange={e => setForm(f => { const v = [...(f.image_views || ['','','','',''])]; v[idx] = e.target.value; return { ...f, image_views: v }; })}
                      >
                        <option value="">{isNl ? '— Weergave —' : '— View tag —'}</option>
                        {IMAGE_VIEW_TAGS.map(t => <option key={t} value={t}>{isNl ? IMAGE_VIEW_LABEL[t] : IMAGE_VIEW_LABEL_EN[t]}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MORE TAB ── */}
      {tab === 'more' && (
        <div>
          <div style={P}>
            <div style={{ color: C.silver2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>{isNl ? 'Leverancier' : 'Distribution'}</div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>{isNl ? 'Naam leverancier' : 'Distributor name'}</label>
              <input style={IS} value={form.distributor_name} onChange={inp('distributor_name')} placeholder={isNl ? 'Bedrijfsnaam...' : 'Company name...'} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>{isNl ? 'Contactpersoon / e-mail' : 'Contact / email'}</label>
              <input style={IS} value={form.distributor_contact} onChange={inp('distributor_contact')} placeholder="email@company.com" />
            </div>
            <div>
              <label style={lbl}>{isNl ? 'Levertijd (dagen)' : 'Lead time (days)'}</label>
              <input style={IS} type="number" min="0" value={form.distributor_lead_days} onChange={inp('distributor_lead_days')} placeholder="7" />
            </div>
          </div>

          {isClothing && (
            <ClothingFields form={form} setForm={setForm} nl={isNl} inputStyle={IS} P={P} />
          )}
        </div>
      )}

      {/* ── ACTIONS ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 24, paddingTop: 16, borderTop: `1px solid ${F.border}` }}>
        {isEdit && (
          <button
            onClick={onDelete}
            style={{ padding: '12px 16px', borderRadius: 12, border: '1px solid #C0505044', background: '#C0505014', color: '#C05050', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
          >
            {isNl ? 'Verwijderen' : 'Delete'}
          </button>
        )}
        <button
          onClick={onSave}
          disabled={saving || !form.name.trim()}
          style={{
            flex: 1, padding: '13px', borderRadius: 12, border: 'none',
            background: (!form.name.trim() || saving) ? `${_T.accent}55` : _T.accent,
            color: '#0a1220', fontSize: 14, fontWeight: 700,
            cursor: (!form.name.trim() || saving) ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
          }}
        >
          {saving ? (isNl ? 'Opslaan…' : 'Saving…') : (isNl ? 'Opslaan' : 'Save')}
        </button>
      </div>
    </div>
  );
}

// ── Main Screen ───────────────────────────────────────────────────────────────
export default function BusinessProductsScreen({ lang, businessId, role, business }) {
  const nl         = lang === 'nl';
  const canEdit    = role === 'owner' || role === 'admin';
  const isClothing = (business?.industry || business?.sector) === 'retail_clothing';

  const [products,    setProducts]    = useState([]);
  const [cats,        setCats]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [statusTab,   setStatusTab]   = useState('all');
  const [showSheet,   setShowSheet]   = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    const key = `sl_cats_${businessId}`;
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) { setCats(parsed); return; }
      }
    } catch (_) {}
    setCats(DEFAULT_CATS);
    localStorage.setItem(key, JSON.stringify(DEFAULT_CATS));
  }, [businessId]);

  const loadProducts = useCallback(() => {
    setLoading(true);
    fetch(`/api/businesses/${businessId}/products`)
      .then(r => r.json())
      .then(data => { setProducts(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const openNew = () => { setForm(EMPTY_FORM); setEditProduct(null); setShowSheet(true); };

  const openEdit = (p) => {
    setForm({
      name:                  p.name                  || '',
      sku:                   p.sku                   || '',
      cat1:                  p.cat1                  || '',
      cat2:                  p.cat2                  || '',
      cat3:                  p.cat3                  || '',
      description:           p.description           || '',
      price:                 p.price                 ?? '',
      sale_price:            p.sale_price            ?? '',
      currency:              p.currency              || 'EUR',
      vat_pct:               p.vat_pct               ?? 21,
      stock_qty:             p.stock_qty             ?? '',
      low_stock_alert:       p.low_stock_alert       ?? '',
      images:                parseImages(p.images),
      image_views:           Array.isArray(p.image_views) ? [...p.image_views, '','','','',''].slice(0,5) : ['','','','',''],
      clothing_meta:         p.clothing_meta         || { sizes: [], colors: [] },
      primary_image:         typeof p.primary_image === 'number' ? p.primary_image : 0,
      distributor_name:      p.distributor_name      || '',
      distributor_contact:   p.distributor_contact   || '',
      distributor_lead_days: p.distributor_lead_days ?? '',
      discount_type:         p.discount_type         || 'pct',
      discount_value:        p.discount_value        ?? '',
      status:                p.status                || 'active',
      barcode:               p.barcode               || '',
      weight_g:              p.weight_g              ?? '',
      tags:                  Array.isArray(p.tags) ? p.tags.join(', ') : (p.tags || ''),
      homepage_published:    !!p.homepage_published,
    });
    setEditProduct(p);
    setShowSheet(true);
  };

  // ── Save ──
  // FIX: send images/image_views/clothing_meta as arrays/objects, NOT JSON strings.
  // The PHP uses is_array() to detect them — sending a pre-serialized string stores NULL.
  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      price:                 form.price                 !== '' ? parseFloat(form.price)               : null,
      sale_price:            form.sale_price            !== '' ? parseFloat(form.sale_price)           : null,
      vat_pct:               Number(form.vat_pct),
      stock_qty:             form.stock_qty             !== '' ? parseInt(form.stock_qty)              : null,
      low_stock_alert:       form.low_stock_alert       !== '' ? parseInt(form.low_stock_alert)        : null,
      distributor_lead_days: form.distributor_lead_days !== '' ? parseInt(form.distributor_lead_days)  : null,
      discount_value:        form.discount_value        !== '' ? parseFloat(form.discount_value)       : null,
      weight_g:              form.weight_g              !== '' ? parseInt(form.weight_g)               : null,
      images:                form.images.filter(x => x.trim() !== ''),                  // array — NOT stringified
      image_views:           (form.image_views || []).slice(0, 5),                      // array — NOT stringified
      clothing_meta:         isClothing ? (form.clothing_meta || { sizes: [], colors: [] }) : null,  // object — NOT stringified
      primary_image:         form.primary_image,
      tags:                  form.tags,                                                 // plain string
      category:              categoryPath(cats, form.cat1, form.cat2, form.cat3),
    };
    try {
      if (editProduct) {
        await fetch(`/api/businesses/${businessId}/products/${editProduct.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      } else {
        const res = await fetch(`/api/businesses/${businessId}/products`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        const data = await res.json();
        // After creation, switch to edit mode so images can be re-uploaded if needed
        if (data?.id) setEditProduct({ id: data.id, ...payload });
      }
    } catch (_) {}
    setSaving(false);
    setShowSheet(false);
    loadProducts();
  };

  const handleDelete = async () => {
    if (!editProduct) return;
    if (!window.confirm(nl ? 'Product verwijderen?' : 'Delete this product?')) return;
    await fetch(`/api/businesses/${businessId}/products/${editProduct.id}`, { method: 'DELETE' }).catch(() => {});
    setShowSheet(false);
    loadProducts();
  };

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || [p.name, p.sku, p.barcode, p.category].some(v => (v || '').toLowerCase().includes(q));
    const matchStatus = statusTab === 'all' || p.status === statusTab;
    return matchSearch && matchStatus;
  });

  const counts = { all: products.length };
  STATUSES.forEach(s => { counts[s] = products.filter(p => p.status === s).length; });

  const tabs = [
    { id: 'all', nl: 'Alle', en: 'All' },
    { id: 'active', nl: 'Actief', en: 'Active' },
    { id: 'draft', nl: 'Concept', en: 'Draft' },
    { id: 'archived', nl: 'Gearchiveerd', en: 'Archived' },
  ];

  return (
    <div style={{ paddingBottom: 32 }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 18, fontWeight: 800 }}>{nl ? 'Producten' : 'Products'}</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>{products.length} {nl ? 'producten' : 'products'}</div>
        </div>
        {canEdit && (
          <button onClick={openNew} style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, padding: '10px 20px', color: '#0a1220', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            + {nl ? 'Product' : 'Product'}
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 14 }}>
        <input style={s_TI({ fontSize: 13, padding: '9px 12px' })} value={search} onChange={e => setSearch(e.target.value)} placeholder={nl ? '🔍 Zoek op naam, SKU, barcode...' : '🔍 Search by name, SKU, barcode...'} />
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, overflowX: 'auto', paddingBottom: 2 }}>
        {tabs.map(t => {
          const active = statusTab === t.id;
          return (
            <button key={t.id} onClick={() => setStatusTab(t.id)} style={{ background: active ? `${_T.accent}22` : _T.bg3, border: `1px solid ${active ? _T.accent : _T.cardBorderBot}`, borderRadius: 20, padding: '6px 14px', cursor: 'pointer', color: active ? _T.accent : C.silver, fontSize: 12, fontWeight: active ? 700 : 400, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {nl ? t.nl : t.en} <span style={{ opacity: 0.65 }}>({counts[t.id] || 0})</span>
            </button>
          );
        })}
      </div>

      {loading && <div style={{ textAlign: 'center', color: C.silver, fontSize: 13, padding: 40 }}>...</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: C.silver, fontSize: 13, padding: 40, background: _T.bg3, borderRadius: 16, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
          {nl ? 'Geen producten gevonden' : 'No products found'}
        </div>
      )}

      {!loading && filtered.map(p => (
        <ProductCard key={p.id} product={p} onClick={canEdit ? () => openEdit(p) : undefined} nl={nl} />
      ))}

      <Sheet open={showSheet} onClose={() => setShowSheet(false)}>
        <ProductForm
          form={form}
          setForm={setForm}
          cats={cats}
          setCats={setCats}
          businessId={businessId}
          isClothing={isClothing}
          onSave={handleSave}
          onDelete={handleDelete}
          onCancel={() => setShowSheet(false)}
          saving={saving}
          isEdit={!!editProduct}
          nl={nl}
        />
      </Sheet>
    </div>
  );
}
